import React, { useState, useEffect } from 'react';
import { parseModbusData } from '../utils/modbusDecoder';
import { getParametersForType } from '../utils/bmsConstants';
import { extractRecordModbus, withAnalysisFlag } from '../utils/telemetrySource';
import { API_BASE } from '../utils/api';
import * as XLSX from 'xlsx';
import TimeSeriesChart from './TimeSeriesChart';
import './HistoricData.css';
import './TimeSeriesChart.css';

function RBMSHistoricData({ authData, selectedDevice, assetConfig, batterySlaveIds, groupDevices }) {
  // Build device list: use groupDevices if available (for selected group),
  // otherwise fall back to authData.devices, then selectedDevice
  const deviceList = groupDevices?.length > 0
    ? groupDevices
    : authData?.devices?.length > 0
      ? authData.devices
      : selectedDevice ? [selectedDevice] : [];

  const [localSelectedDevice, setLocalSelectedDevice] = useState(selectedDevice?.deviceId || '');
  const [selectedBatteryIndex, setSelectedBatteryIndex] = useState(0);

  // Set default times: endTime = now, startTime = 6 hours earlier
  const getDefaultTimes = () => {
    const now = new Date();
    const sixHoursAgo = new Date(now.getTime() - (6 * 60 * 60 * 1000));

    // Format to local datetime string (YYYY-MM-DDTHH:MM)
    const formatLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return {
      start: formatLocal(sixHoursAgo),
      end: formatLocal(now)
    };
  };

  const defaultTimes = getDefaultTimes();
  const [startTime, setStartTime] = useState(defaultTimes.start);
  const [endTime, setEndTime] = useState(defaultTimes.end);
  const [historicData, setHistoricData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [viewMode, setViewMode] = useState('table');
  const [filterOutliers, setFilterOutliers] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Chart parameter groupings
  const chartGroups = [
    {
      title: 'Total Voltage',
      yAxisLabel: 'Volts (V)',
      parameters: [
        { key: 'totalVoltage', name: 'Total Voltage (V)' },
      ],
    },
    {
      title: 'Cell Voltage',
      yAxisLabel: 'mV',
      parameters: [
        { key: 'maxCellVoltage', name: 'Max Cell Voltage (mV)' },
        { key: 'minCellVoltage', name: 'Min Cell Voltage (mV)' },
      ],
    },
    {
      title: 'Current',
      yAxisLabel: 'Amps (A)',
      parameters: [
        { key: 'current', name: 'Current (A)' },
      ],
    },
    {
      title: 'State of Charge / Health',
      yAxisLabel: '%',
      parameters: [
        { key: 'soc', name: 'SOC (%)' },
        { key: 'soh', name: 'SOH (%)' },
      ],
    },
    {
      title: 'Temperature',
      yAxisLabel: '°C',
      parameters: [
        { key: 'maxTemp', name: 'Max Temp (°C)' },
        { key: 'minTemp', name: 'Min Temp (°C)' },
      ],
    },
  ];


  // Convert local datetime to ISO string for API
  const toISOString = (localDateTime) => {
    const date = new Date(localDateTime);
    return date.toISOString();
  };

  const currentParams = getParametersForType(assetConfig?.type);

  const fetchData = async () => {
    if (!authData || !authData.token) {
      setError('Not authenticated. Please login again.');
      return;
    }

    if (!localSelectedDevice) {
      setError('Please select a device.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startDate = new Date(startTime);
      const endDate = new Date(endTime);
      const timeDiffHours = (endDate - startDate) / (1000 * 60 * 60);

      let allProcessedData = [];

      // If interval > 24 hours, split into chunks
      if (timeDiffHours > 24) {
        const chunks = [];
        let currentStart = new Date(startDate);

        while (currentStart < endDate) {
          const currentEnd = new Date(currentStart.getTime() + (24 * 60 * 60 * 1000));
          const chunkEnd = currentEnd > endDate ? endDate : currentEnd;

          chunks.push({
            start: currentStart.toISOString(),
            end: chunkEnd.toISOString()
          });

          currentStart = chunkEnd;
        }

        // Fetch data for each chunk
        for (const chunk of chunks) {
          const rawUrl = `${API_BASE}/api/getLocationInterval?api_access_token=${authData.token}&deviceId=${localSelectedDevice}&startDate=${chunk.start}&endDate=${chunk.end}`;
          const url = withAnalysisFlag(rawUrl);

          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          let processedData = [];
          if (data && data.locationInfo && Array.isArray(data.locationInfo)) {
            processedData = data.locationInfo;
          } else if (Array.isArray(data)) {
            processedData = data;
          }

          allProcessedData = [...allProcessedData, ...processedData];
        }
      } else {
        // Single API call for intervals <= 24 hours
        const startTimeISO = toISOString(startTime);
        const endTimeISO = toISOString(endTime);

        const rawUrl = `${API_BASE}/api/getLocationInterval?api_access_token=${authData.token}&deviceId=${localSelectedDevice}&startDate=${startTimeISO}&endDate=${endTimeISO}`;
        const url = withAnalysisFlag(rawUrl);

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data && data.locationInfo && Array.isArray(data.locationInfo)) {
          allProcessedData = data.locationInfo;
        } else if (Array.isArray(data)) {
          allProcessedData = data;
        }
      }

      // Process each location record
      const tableData = allProcessedData.map(item => {
        const row = {
          timestamp: item.timestamp,
          latitude: item.latitude,
          longitude: item.longitude,
          speed: item.speed
        };

        // Parse modbus data if available
        let modbusArray = [];
        const slaveId = batterySlaveIds ? batterySlaveIds[selectedBatteryIndex] : null;
        const rs485modbus = extractRecordModbus(item);
        if (rs485modbus) {
          modbusArray = parseModbusData(rs485modbus, slaveId, assetConfig?.type);
        }

        // Extract parameters from parsed modbus data
        currentParams.forEach(param => {
          const found = modbusArray.find(reg => reg.address === param.address);
          if (found) {
            let displayValue = found.parsedValue;
            let displayUnit = found.unit;
            // Handle fault bitmap arrays
            if (param.isFault && Array.isArray(found.parsedValue)) {
              const faults = found.parsedValue.filter(f =>
                f !== 'No Alarms' && f !== 'No Protection Events'
              );
              displayValue = faults.length > 0 ? faults.join(', ') : '';
              displayUnit = '';
            }
            row[param.key] = {
              value: displayValue,
              unit: displayUnit,
              description: found.description
            };
          } else {
            row[param.key] = { value: 'N/A', unit: '', description: 'N/A' };
          }
        });

        return row;
      });

      setHistoricData(tableData);


    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    let sortableData = [...historicData];
    if (sortConfig.key !== null) {
      sortableData.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'timestamp') {
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
        } else if (sortConfig.key === 'latitude' || sortConfig.key === 'longitude') {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        } else {
          // For RBMS parameters
          aValue = parseFloat(a[sortConfig.key]?.value) || 0;
          bValue = parseFloat(b[sortConfig.key]?.value) || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [historicData, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  // Export to Excel
  const exportToExcel = () => {
    const worksheetData = sortedData.map(row => {
      const exportRow = {
        'Timestamp': formatDateTime(row.timestamp),
        'Latitude': row.latitude,
        'Longitude': row.longitude,
      };

      currentParams.forEach(param => {
        const data = row[param.key];
        exportRow[param.name] = `${data?.value} ${data?.unit}`.trim();
      });

      return exportRow;
    });


    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'RBMS Data');

    // Generate filename with timestamp
    const now = new Date();
    const filename = `RBMS_Data_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  // Reset page when data changes or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [historicData, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = sortedData.slice(startIndex, startIndex + rowsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="historic-data-view">
      <h2>RBMS Historic Data</h2>
      <p className="subtitle">View top-level RBMS parameters over time</p>

      <form onSubmit={handleSubmit} className="input-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="device">Select Battery:</label>
            <select
              id="device"
              value={localSelectedDevice}
              onChange={(e) => setLocalSelectedDevice(e.target.value)}
              required
            >
              <option value="">-- Select a vehicle --</option>
              {deviceList.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.vehicleId} ({device.deviceId})
                </option>
              ))}
            </select>
          </div>

          {batterySlaveIds && batterySlaveIds.length > 1 && (
            <div className="form-group">
              <label htmlFor="battery">Battery:</label>
              <select
                id="battery"
                value={selectedBatteryIndex}
                onChange={(e) => setSelectedBatteryIndex(parseInt(e.target.value))}
              >
                {batterySlaveIds.map((slaveId, index) => (
                  <option key={slaveId} value={index}>
                    Battery {index + 1} (Slave {slaveId})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startTime">Start Time (Local):</label>
            <input
              type="datetime-local"
              id="startTime"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="endTime">End Time (Local):</label>
            <input
              type="datetime-local"
              id="endTime"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </div>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Loading...' : 'Fetch Data'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {historicData.length > 0 && (
        <div className="results">
          <div className="results-header">
            <h3>RBMS Data ({historicData.length} records)</h3>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <div className="view-toggle">
                <button
                  className={viewMode === 'table' ? 'active' : ''}
                  onClick={() => setViewMode('table')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="3" y1="15" x2="21" y2="15" />
                    <line x1="9" y1="3" x2="9" y2="21" />
                  </svg>
                  Table
                </button>
                <button
                  className={viewMode === 'charts' ? 'active' : ''}
                  onClick={() => setViewMode('charts')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                  Charts
                </button>
              </div>
              <button onClick={exportToExcel} className="export-button">
                Export to Excel
              </button>
            </div>
          </div>

          {viewMode === 'charts' && (
            <div style={{ width: '100%' }}>
              <div style={{ marginBottom: '1.25rem', display: 'flex', justifyContent: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#8892b0', fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={filterOutliers}
                    onChange={(e) => setFilterOutliers(e.target.checked)}
                    style={{ accentColor: '#638cff', width: '14px', height: '14px', cursor: 'pointer' }}
                  />
                  Clean Outlier Spikes
                </label>
              </div>
              <div className="charts-grid">
                {chartGroups.map((group, idx) => (
                  <TimeSeriesChart
                    key={idx}
                    data={historicData}
                    parameters={group.parameters}
                    title={group.title}
                    yAxisLabel={group.yAxisLabel}
                    filterOutliers={filterOutliers}
                  />
                ))}
              </div>
            </div>
          )}

          {viewMode === 'table' && (
            <div className="table-container">
              <table className="historic-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('timestamp')} style={{ cursor: 'pointer' }}>
                      Timestamp{getSortIndicator('timestamp')}
                    </th>
                    <th onClick={() => handleSort('latitude')} style={{ cursor: 'pointer' }}>
                      Location{getSortIndicator('latitude')}
                    </th>
                    {currentParams.map(param => (
                      <th key={param.key} onClick={() => handleSort(param.key)} style={{ cursor: 'pointer' }}>
                        {param.name}{getSortIndicator(param.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((row, index) => (
                    <tr key={startIndex + index}>
                      <td className="timestamp-cell">{formatDateTime(row.timestamp)}</td>
                      <td className="location-cell">
                        {row.latitude?.toFixed(6)}, {row.longitude?.toFixed(6)}
                      </td>
                      {currentParams.map(param => {
                        const data = row[param.key];
                        const hasFault = param.isFault && data?.value && data.value !== 'N/A';
                        return (
                          <td key={param.key} title={data?.description}
                            style={hasFault ? { color: '#ef4444', fontWeight: 600 } : undefined}
                          >
                            {data?.value} {data?.unit}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>


              <div className="pagination-controls">
                <div className="rows-per-page">
                  <label htmlFor="rowsPerPage">Rows per page:</label>
                  <select id="rowsPerPage" value={rowsPerPage} onChange={handleRowsPerPageChange}>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={sortedData.length}>All</option>
                  </select>
                </div>
                <div className="page-info">
                  {startIndex + 1}-{Math.min(startIndex + rowsPerPage, sortedData.length)} of {sortedData.length}
                </div>
                <div className="page-nav">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="nav-button"
                  >
                    Previous
                  </button>
                  <span className="page-number">Page {currentPage} of {totalPages}</span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="nav-button"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && historicData.length === 0 && !error && (
        <div className="no-data">
          <p>No data loaded. Enter device information and click "Fetch Data".</p>
        </div>
      )}
    </div>
  );
}

export default RBMSHistoricData;
